import { EventObject } from '@xstate/store-react';
import { AppRoute } from '../../../enums/appRoutes';
import { ConfirmationPurchaseData } from '../../../models/page/confirmationPageState';
import { StoreContext } from '../../../models/store/context';
import { CheckoutSuccess } from '../../../pages/CheckoutPage/CheckoutResponses';

import { saveSession } from '../../../services/session/save';
import { log } from '@domgen/shared/utils/log';
import { StoreEvent } from '../../../enums/storeEvent';

/**
 * Finalizes successful checkout by deriving purchase confirmation data.
 *
 * Persists final confirmation state, triggers downstream tracking, and routes to
 * confirmation. Also posts contract registration when a plan number is available.
 *
 * @param context Current store context.
 * @param event Event optionally containing checkout success payload.
 * @param enqueue Store enqueue helpers used for side effects and navigation emission.
 * @returns Updated context when success data exists; otherwise no state change.
 */
export const checkoutSuccess = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data?: CheckoutSuccess },
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext | void => {
  log.debug({ message: StoreEvent.CHECKOUT_SUCCESS, data: event.data });
  const quote = context.quote?.data;
  const checkoutSuccessData = event.data;

  if (quote && checkoutSuccessData) {
    const paymentType = checkoutSuccessData.billing.paymentMethodUsed;
    const mappedPaymentType = paymentType === 'card' ? 'cc' : 'ach';
    const planNumber = checkoutSuccessData.contracts[0].contractId;
    const transactionId = checkoutSuccessData.transactionId;

    const { monthly_subs, fixed_term_single_payment } = quote.offer.contracts[0].contractAttributes;

    /** This is the price that someone has paid from quote presented to them - assumption at the moment is there will be only fixed term or monthly subscription */
    const offerTakenUp = fixed_term_single_payment
      ? fixed_term_single_payment[mappedPaymentType]
      : monthly_subs
        ? monthly_subs[mappedPaymentType]
        : undefined;

    if (!offerTakenUp) {
      log.error({
        message: 'Checkout success event received but no offer data found in quote',
        data: { quote, checkoutSuccessData },
      });
      enqueue.emit.navigate({ path: AppRoute.ERROR, replace: true });
    }

    const purchaseData: ConfirmationPurchaseData = {
      planType: fixed_term_single_payment ? 'replacement-care' : 'appliance-care',
      planNumber,
      transactionId,
      paymentType,
      mappedPaymentType,
      netFirstPaymentAmountFromPurchase: offerTakenUp?.netFirstPaymentAmount,
      netFirstPaymentAmountAfterPromotionalPeriodFromPurchase:
        offerTakenUp?.discountProfileAttributes?.netFirstPaymentAmountAfterPromotionalPeriod,
      discountCodeFromPurchase:
        offerTakenUp?.discountProfileAttributes?.[
          mappedPaymentType === 'cc' ? 'ccDiscountCodeApplied' : 'achDiscountCodeApplied'
        ],
      discountTermFromPurchase: offerTakenUp?.discountProfileAttributes?.discountTerm,
    };

    const guarantee = context.quote?.data?.items?.[0]?.guarantee;

    const { ...confirmation } = context.confirmation || {};

    const updatedContext = {
      ...context,
      form: {},
      applianceLookupResults: undefined,
      quote: undefined,
      checkout: undefined,
      confirmation: {
        ...confirmation,
        purchaseData,
        regOnly: undefined,
        applianceDetails: {
          ...confirmation?.applianceDetails,
          guarantee: guarantee ?? 12,
        },
      },
    } satisfies StoreContext;

    saveSession(updatedContext);
    enqueue.emit.navigate({ path: AppRoute.CONFIRMATION, replace: true });

    return updatedContext;
  }
};
